import type { Order, OrderLineItem } from "../types";
import { parseLineItemPriceInCents } from "../utils/get-line-item-price";

export type PaidPriceOptions = {
  /** Add the taxes that are not already embedded in `price` (HT shops). */
  includeTaxes?: boolean;
  /** Subtract the order-level discounts allocated to this line item. */
  includeDiscounts?: boolean;
};

/**
 * Unit price actually paid for a line item, in cents.
 *
 * Transposition of `apps/return-widget/src/utils/get-paid-price.ts` so the new
 * portal announces the same amounts as the legacy one (BBS-309):
 * - `exchangeOriginPaidPrice` short-circuits everything when the order is
 *   itself a Baback exchange order (mirrors `PricingComputationService.getPaidPrice`),
 * - `unitTaxes[].amount` is already per unit — `ShopifyOrderNormalizeDto` divides
 *   Shopify's whole-line `taxLines[].priceSet` by `lineItem.quantity` before it ever
 *   reaches the portal — so it is added as is, like the backend and the legacy portal do,
 * - each allocated discount is a whole-line amount, so it is spread over `quantity_bought`.
 */
export function getPaidPriceInCents(
  lineItem: OrderLineItem | undefined,
  { includeTaxes = true, includeDiscounts = true }: PaidPriceOptions = {},
): number {
  if (!lineItem) return 0;

  const exchangeOrigin = parseLineItemPriceInCents(
    lineItem.exchangeOriginPaidPrice,
  );
  if (lineItem.exchangeOriginPaidPrice && exchangeOrigin !== null) {
    return exchangeOrigin;
  }

  const price = parseLineItemPriceInCents(lineItem.price);
  if (price === null) return 0;

  const quantityBought = lineItem.quantity_bought || 1;
  let paidPrice = price;

  if (includeTaxes) {
    const additionalTax = lineItem.unitTaxes
      .filter((tax) => !tax.includedInPrice)
      .reduce((sum, tax) => sum + tax.amount, 0);
    paidPrice += Math.round(additionalTax * 100);
  }

  if (includeDiscounts) {
    const allocated = lineItem.discountsAllocated.reduce(
      (sum, discount) => sum + discount.amount,
      0,
    );
    paidPrice -= Math.round((allocated * 100) / quantityBought);
  }

  return paidPrice;
}

export function findOrderLineItem(
  order: Order | null | undefined,
  lineItemId: string,
): OrderLineItem | undefined {
  return order?.lineItems?.find((li) => li.line_item_id === lineItemId);
}

export function getLineItemPaidPriceInCents(
  order: Order | null | undefined,
  lineItemId: string,
  options: PaidPriceOptions = {},
): number {
  return getPaidPriceInCents(findOrderLineItem(order, lineItemId), options);
}
